/**
 * Build orchestration WebSocket payloads with minimum necessary PHI and auth fields.
 */

import { getJwtTokenCookie, shouldUseServerSessionAuth } from "./authCookie";
import { getStoredOrchestrationSessionId } from "../Apis/AIPrescriptionApis/orchestrationSession";
import {
  getPatientId,
  getPatientName,
} from "./patientSession";
import { devError } from "./safeClientLog";
import { getReactEnvValue } from "./envConfig";

/** Match orchestration EXPOSE_PATIENT_NAME_TO_LLM default (false). */
const _sendPatientNameEnv = (
  getReactEnvValue("REACT_APP_SEND_PATIENT_NAME", "false")
)
  .trim()
  .toLowerCase();
export const SEND_PATIENT_NAME_TO_ORCHESTRATION = ["true", "1", "yes"].includes(
  _sendPatientNameEnv
);

export function resetOrchestrationSocketJwtBinding(): void {
  /* Reserved for WebSocket reconnect hooks; JWT is sent on every process when available. */
}

/**
 * The JWT to send on `process` messages. Blank ONLY when the Hub owns the session and injects the
 * credential server-side. Keyed on the auth mode rather than on "is this a strict environment":
 * a DEV/STAGE build running in direct mode holds the JWT in the browser and must forward it, or
 * every MCP tool call fails auth even though login succeeded.
 */
export function getOrchestrationJwtToken(): string {
  if (shouldUseServerSessionAuth()) {
    return "";
  }
  return getJwtTokenCookie();
}

export type ProcessPayloadInput = {
  type?: string;
  text: string;
  patientUniqueIdentifier?: string;
  patientName?: string;
  doctorId?: string;
  providerLocationId?: string;
  loginUserId?: string;
  sessionId?: string;
  vttContent?: string;
  jwtToken?: string;
  [key: string]: unknown;
};

/**
 * Attach auth/PHI fields for a `process` (or similar) message.
 * Includes jwtToken whenever available (required by drx-mcp tool auth).
 */
export function buildOrchestrationProcessPayload(
  input: ProcessPayloadInput
): Record<string, unknown> {
  const text = (input.text ?? "").trim();
  const patientUid =
    (input.patientUniqueIdentifier || "").trim() || getPatientId();
  const sid =
    (input.sessionId || "").trim() || getStoredOrchestrationSessionId() || "";
  const loginUserId = (input.loginUserId || "").trim();
  const doctorId = (input.doctorId || "").trim();
  const providerLocationId = (input.providerLocationId || "").trim();
  const vtt = (input.vttContent || "").trim();

  const payload: Record<string, unknown> = {
    type: input.type || "process",
    text,
    patientUniqueIdentifier: patientUid,
    doctorId,
    providerLocationId,
  };

  if (SEND_PATIENT_NAME_TO_ORCHESTRATION) {
    const name =
      (input.patientName || "").trim() || getPatientName();
    if (name) {
      payload.patientName = name;
    }
  }

  const jwt = shouldUseServerSessionAuth()
    ? ""
    : (input.jwtToken || "").trim() || getOrchestrationJwtToken();
  if (jwt) {
    payload.jwtToken = jwt;
  } else if (process.env.NODE_ENV === "development") {
    devError(
      "JWT missing — WebSocket process will fail MCP auth. Sign in again at http://localhost:3000 (not 127.0.0.1), or set JWT_TOKEN / re-login after pulling latest app code."
    );
  }

  if (sid) {
    payload.sessionId = sid;
  }
  if (vtt) {
    payload.vttContent = vtt;
  }
  if (loginUserId) {
    payload.loginUserId = loginUserId;
  }

  const { type, text: _t, patientUniqueIdentifier: _p, ...rest } = input;
  console.log("[buildOrchestrationProcessPayload] Rest fields:", Object.keys(rest), "sendOrigin value:", rest.sendOrigin);
  for (const [key, value] of Object.entries(rest)) {
    if (
      value === undefined ||
      key === "jwtToken" ||
      key === "patientName" ||
      key === "sessionId" ||
      key === "vttContent" ||
      key === "loginUserId" ||
      key === "doctorId" ||
      key === "providerLocationId"
    ) {
      console.log(`[buildOrchestrationProcessPayload] Skipping key: ${key}, value: ${value}`);
      continue;
    }
    if (!(key in payload)) {
      console.log(`[buildOrchestrationProcessPayload] Adding key: ${key}, value: ${value}`);
      payload[key] = value;
    }
  }
  console.log("[buildOrchestrationProcessPayload] Final payload sendOrigin:", payload.sendOrigin);

  return payload;
}
